import React, { useEffect, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { Info } from 'react-feather';
import { Api, updateUserData, createReferralCode } from '../../api';
import {
  getNotificationSetting,
  unsubscribeUserNotification,
  updateUserNotificationSetting
} from '../../api/notificationsp-api';
import { useAuthContext, useGeneralContext } from '../../context';
import PersonalJumbleEmail from './PersonalJumbleEmail';
import { UpgradePremiumButton, UpgradeToPremiumButton } from './PremiumButton';
import Navigation from '../../components/Nav/Nav';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import { CopyPasteString } from '../../components/CopyPasteString';

function Settings() {
  const context = useGeneralContext();
  const authContext = useAuthContext();
  const { user } = authContext;
  const { nickname, setFeedbackModalOpen, setPlansModalOpen, subscriptionStatus, referralCode, setReferralCode } =
    context;
  const [showModal, setShowModal] = useState(false);
  const location = useLocation<{
    destination: string;
  }>();

  const isPremium = subscriptionStatus && subscriptionStatus !== 'free';
  const isYearly = subscriptionStatus === 'premium_yearly';
  const isLifetime = subscriptionStatus === 'lifetime';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (location.state && location.state.destination === 'choose-plan') {
      setPlansModalOpen(true);
    }
  }, [location.state, setPlansModalOpen]);

  // email/pwd and google create different user objects
  const userEmail = user?.attributes?.email || (user as any)?.signInUserSession?.idToken?.payload?.email || null;
  return (
    <div className="tw-col-span-full tw-grid tw-h-full tw-grid-cols-[minmax(0,1fr)] lg:tw-grid-cols-[17rem_minmax(0,1fr)] tw-bg-[url('../../public/bg.png')] tw-bg-cover tw-bg-no-repeat tw-bg-center">
      <InfoModal showModal={showModal} setShowModal={setShowModal} />
      <Navigation />
      <div className="lg:tw-col-start-2 tw-px-4 tw-pt-20 tw-pb-10 sm:tw-px-6 lg:tw-p-10">
        <div className="tw-mx-auto tw-w-full tw-max-w-content tw-rounded-2xl tw-border-2 tw-border-white tw-bg-white/80 tw-px-4 tw-py-6 sm:tw-px-8 lg:tw-px-12 lg:tw-py-10">
          <h1 className="tw-m-0 tw-font-normal tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl">Settings</h1>

          <div className="tw-mt-8 tw-rounded-2xl tw-bg-primary-50 tw-p-6 lg:tw-p-8">
            <div className="tw-flex tw-flex-col tw-gap-4 tw-justify-between lg:tw-flex-row lg:tw-items-center">
              <div className="tw-text-lg tw-font-medium">
                {`You are currently on the ${
                  isPremium
                    ? isYearly
                      ? 'annual premium plan'
                      : isLifetime
                      ? 'lifetime premium plan'
                      : 'monthly premium plan'
                    : 'free plan'
                }`}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center">
                <button
                  onClick={() => {
                    setFeedbackModalOpen(true);
                  }}
                  className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-xl tw-border tw-border-primary-600 tw-bg-primary-50 tw-px-6 tw-min-h-control tw-text-base tw-cursor-pointer"
                >
                  <MailIcon />
                  <span className="tw-font-semibold tw-bg-gradient-to-r tw-from-primary-500 tw-to-secondary-500 tw-bg-clip-text tw-text-transparent">
                    Chat with us
                  </span>
                </button>
                {isPremium ? (
                  isYearly || isLifetime ? (
                    <></>
                  ) : (
                    <UpgradeToPremiumButton
                      key="UpgradeToPremiumButton"
                      header=""
                      buttons={[
                        {
                          text: 'Upgrade to Annual',
                          planId: 'premium_yearly',
                          isUpgrade: true,
                          // whitespace-nowrap: at lg these two buttons sat side by
                          // side in a column narrow enough to break both labels
                          // onto a second line.
                          classNames:
                            'tw-flex tw-w-full tw-justify-center tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-xl tw-bg-gradient-to-b tw-from-primary-500 tw-to-secondary-500 tw-px-6 tw-min-h-control tw-font-semibold tw-text-white tw-text-base tw-border-none tw-cursor-pointer'
                        }
                      ]}
                    />
                  )
                ) : (
                  <button
                    className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-xl tw-bg-gradient-to-b tw-from-primary-500 tw-to-secondary-500 tw-px-6 tw-min-h-control tw-font-semibold tw-text-white tw-text-base tw-border-none tw-cursor-pointer"
                    onClick={() => setPlansModalOpen(true)}
                  >
                    Upgrade to premium
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="tw-mt-8">
            <SettingsRow label="You are logged in as" description={userEmail} />
            <SettingsRow
              label="Name"
              description={`What should we call you${nickname ? `, ${nickname}` : ''}?`}
              control={<Nickname />}
            />
            <SettingsRow
              label="Email subscription"
              description="Subscribe to receive product updates, special offers, and journaling tips"
              control={<SubscribeEmailList />}
            />
            <SettingsRow
              label="Journal Analysis Email"
              description="(✨ New Premium Feature) Subscribe to receive journal analysis emails with extracts from your daily journal entry"
              control={<ToggleExtractsEmailSubscription />}
            />
            <SettingsRow
              label="Your Jumble email"
              labelAdornment={
                <Info
                  onClick={() => {
                    setShowModal(true);
                  }}
                  className="tw-cursor-pointer tw-w-5 tw-h-5 tw-text-primary-700"
                />
              }
              description="(✨ Premium Feature) Journal by hand. Email a clear image of the entry to your personal Jumble email address and it will be added to your journal."
              control={<PersonalJumbleEmail />}
            />
            <SettingsRow
              label="Your referral link"
              description="Your personal invite link — share it and gift a free month to your friends"
              control={<ReferralCodeSection referralCode={referralCode} setReferralCode={setReferralCode} />}
            />
            <SettingsRow
              label="Subscription plan"
              description={
                isPremium
                  ? isYearly
                    ? 'View details and manage your subscription'
                    : isLifetime
                    ? 'You have Jumble premium for life'
                    : 'Upgrade to annual and save 25%'
                  : 'Get access to the journal assistant, reMarkable integration, AI assisted self exploration and more by upgrading to Premium!'
              }
              control={<UpgradePremiumButton />}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Every settings row was hand-assembled from the same nine divs, which is how
// the dividers ended up with 75px above and 36px below, and how the label ended
// up larger than the value it labels.
const SettingsRow = (props: {
  label: string;
  description?: string | null;
  labelAdornment?: React.ReactNode;
  control?: React.ReactNode;
}) => {
  const { label, description, labelAdornment, control } = props;
  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-py-6 tw-border-t tw-border-neutral-200 lg:tw-flex-row lg:tw-items-center lg:tw-gap-8">
      <div className="tw-flex tw-flex-col tw-gap-1 lg:tw-w-80 lg:tw-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-neutral-900">
          {label}
          {labelAdornment}
        </div>
        {description && <div className="tw-text-sm tw-text-neutral-500">{description}</div>}
      </div>
      {control && <div className="tw-flex tw-flex-col tw-gap-2 tw-flex-1 tw-min-w-0">{control}</div>}
    </div>
  );
};

const MailIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
      <path
        d="M2.66732 3.16602H13.334C14.0673 3.16602 14.6673 3.76602 14.6673 4.49935V12.4993C14.6673 13.2327 14.0673 13.8327 13.334 13.8327H2.66732C1.93398 13.8327 1.33398 13.2327 1.33398 12.4993V4.49935C1.33398 3.76602 1.93398 3.16602 2.66732 3.16602Z"
        stroke="url(#paint0_linear_6643_17421)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.6673 4.5L8.00065 9.16667L1.33398 4.5"
        stroke="url(#paint1_linear_6643_17421)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id="paint0_linear_6643_17421"
          x1="8.00065"
          y1="3.16601"
          x2="28.5188"
          y2="34.0732"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#7543E8" />
          <stop offset="1" stopColor="#FF5B5B" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_6643_17421"
          x1="8.00065"
          y1="4.5"
          x2="13.2186"
          y2="22.4657"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#7543E8" />
          <stop offset="1" stopColor="#FF5B5B" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const UNSUBSCRIBE_STRING = 'Unsubscribe from Reminders';
const options = new Array(25)
  .fill('')
  .map((_v, i) => (i === 0 ? 'MIDNIGHT' : i === 12 ? 'MIDDAY' : i < 12 ? `${i} AM` : `${i - 12} PM`));
options[24] = UNSUBSCRIBE_STRING;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TimeSelect = () => {
  const [notificationSetting, setNotificationSetting] = useState(24);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('When do you want your daily email reminder to go out?');

  useEffect(() => {
    const controller = new AbortController();
    const retrieveNotificationSetting = async () => {
      setLoading(true);
      try {
        const data = await getNotificationSetting(controller.signal);
        setNotificationSetting(data.time || 24);
      } catch (error: any) {
        // request was cancelled
        if (error.code === 'ERR_CANCELED') {
          console.log('request cancelled');
          return;
        }
        alert(
          'Failed to retrieve notification setting. Please try again and reach out to support@jumblejournal.org if the problem persists'
        );
      }
      setLoading(false);
    };
    retrieveNotificationSetting();
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <>
      <div className="text text--bold text--paragraph-2 text--neutral-500">{title}</div>
      <div className="settings__selector-wrapper">
        {loading ? (
          <div style={{ gridColumn: 2 }}>
            <span
              style={{ textAlign: 'center', height: '1em', width: '1em' }}
              className="spinner-border"
              role="status"
              aria-hidden="true"
            ></span>
          </div>
        ) : (
          <Form.Select
            onMouseEnter={(e) => ((e.target as any).style.cursor = 'pointer')} // Change cursor to pointer on hover
            onMouseLeave={(e) => ((e.target as any).style.cursor = 'default')} // Revert cursor to default when leaving
            value={options[notificationSetting]}
            aria-label="Notification Time Select"
            onChange={async (event: React.ChangeEvent<HTMLSelectElement>) => {
              event.preventDefault();
              const choice = event.target.value;
              setLoading(true);
              try {
                if (choice === UNSUBSCRIBE_STRING) {
                  await unsubscribeUserNotification();
                  setTitle('✅ You will not be sent a daily reminder.');
                  setNotificationSetting(24);
                } else {
                  const time = options.indexOf(choice);
                  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                  await updateUserNotificationSetting({ time, timezone });
                  setTitle(`✅ You will be emailed a daily reminder at ${options[time]} Local Time.`);
                  setNotificationSetting(time);
                }
              } catch (error) {
                alert(
                  'Failed to update notification setting. Please try again and reach out to support@jumblejournal.org if the problem persists'
                );
              }
              setLoading(false);
            }}
          >
            {options.map((v, i) => (
              <option key={`option-${i}`}>{v}</option>
            ))}
          </Form.Select>
        )}
      </div>
    </>
  );
};

const resetForm = () => {
  const element = document.getElementById('account-creation-form') as HTMLFormElement;
  if (element !== null) {
    element.reset();
  }
};

const SubscribeEmailList = () => {
  const [loading, setLoading] = useState(false);
  const context = useGeneralContext();
  const { setSubscribed, subscribed } = context;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    if (event === undefined) {
      return;
    }
    const isChecked = event.target.checked;
    setSubscribed(isChecked);
    try {
      setLoading(true);

      if (isChecked) {
        await Api.post('/subscribe');
      } else {
        await Api.post('/unsubscribe');
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setSubscribed(!isChecked);
      window.alert(
        'There was a problem updating subscription setting. Please try again. If the issue persists, let us know at support@jumblejournal.org.'
      );
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-start">
      <Form.Check
        disabled={loading}
        type="switch"
        id="custom-switch"
        checked={subscribed}
        onChange={handleCheckboxChange}
        onMouseEnter={(e) => ((e.target as any).style.cursor = 'pointer')} // Change cursor to pointer on hover
        onMouseLeave={(e) => ((e.target as any).style.cursor = 'default')} // Revert cursor to default when leaving
      />
      <div className={`tw-text-base tw-font-semibold ${loading ? 'tw-text-neutral-500' : 'tw-text-neutral-700'}`}>
        {loading ? 'updating ...' : subscribed ? 'Subscribed' : 'Unsubscribed'}
      </div>
    </div>
  );
};

const ToggleExtractsEmailSubscription = () => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const context = useGeneralContext();
  const { setSubscribedToExtractsEmail, subscribedToExtractsEmail, subscriptionStatus } = context;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    if (event === undefined) {
      return;
    }
    if (subscriptionStatus === 'free') {
      setShowModal(true);
      return;
    }
    const isChecked = event.target.checked;
    setSubscribedToExtractsEmail(isChecked);
    try {
      setLoading(true);

      await updateUserData({ subscribedToExtractsEmail: isChecked });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setSubscribedToExtractsEmail(!isChecked);
      window.alert(
        'There was a problem updating setting for user subscription to journal analysis emails. Please try again. If the issue persists, let us know at support@jumblejournal.org.'
      );
    }
  };

  return (
    <>
      <PremiumUpsellModal
        showModal={showModal}
        setShowModal={setShowModal}
        message="You need to upgrade to Premium to subscribe to journal analysis emails."
      />
      <div className="tw-flex tw-flex-col tw-items-start">
        <Form.Check
          disabled={loading}
          type="switch"
          id="custom-switch"
          checked={subscribedToExtractsEmail}
          onChange={handleCheckboxChange}
          onMouseEnter={(e) => ((e.target as any).style.cursor = 'pointer')} // Change cursor to pointer on hover
          onMouseLeave={(e) => ((e.target as any).style.cursor = 'default')} // Revert cursor to default when leaving
        />
        <div className={`tw-text-base tw-font-semibold ${loading ? 'tw-text-neutral-500' : 'tw-text-neutral-700'}`}>
          {loading ? 'updating ...' : subscribedToExtractsEmail ? 'Subscribed' : 'Unsubscribed'}
        </div>
      </div>
    </>
  );
};

const Nickname = () => {
  const context = useGeneralContext();
  const { setNickname, nickname } = context;
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.target);
    const formDataObj = Object.fromEntries(formData.entries());

    const newNickname = formDataObj.nickname as string;

    if (newNickname === nickname) {
      resetForm();
      return;
    }
    setIsLoading(true);

    if (newNickname.length > 32) {
      window.alert('Sorry, this nickname is too long. Try something shorter.');
      setIsLoading(false);
      return;
    }

    try {
      await updateUserData({ nickname: newNickname });
      setNickname(newNickname.toString());
    } catch (error) {
      alert(
        'Failed to update nickame. Please try again and reach out to support@jumblejournal.org if the problem persists'
      );
    }

    setIsLoading(false);
    resetForm();
  };

  return (
    <div className="tw-w-full tw-max-w-md">
      <div className="tw-w-full">
        <Form id="account-creation-form" onSubmit={handleSubmit}>
          <InputGroup>
            <Form.Control
              aria-label="Update nickname form"
              placeholder="change your name..."
              name="nickname"
              id="nickname"
              className="tw-min-h-control"
            />
            <Button
              className="tw-bg-white tw-border tw-border-neutral-300 tw-text-neutral-700 hover:tw-bg-neutral-100 tw-min-h-control tw-px-4 tw-whitespace-nowrap"
              type="submit"
              variant="outline-secondary"
              id="button-addon2"
            >
              {isLoading && (
                <span className="spinner-grow spinner-grow-sm tw-mr-2 tw-h-4" role="status" aria-hidden="true"></span>
              )}
              Update
            </Button>
          </InputGroup>
        </Form>
      </div>
    </div>
  );
};

const ReferralCodeSection = (props: {
  referralCode: string | null;
  setReferralCode: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  const { referralCode, setReferralCode } = props;
  const [isLoading, setIsLoading] = useState(false);

  const handleCreateReferralCode = async () => {
    setIsLoading(true);
    try {
      const response = await createReferralCode();
      if (response.data && response.data.referralCode) {
        setReferralCode(response.data.referralCode);
      }
    } catch (error) {
      console.error('Error creating referral code:', error);
      alert(
        'Failed to create referral code. Please try again and reach out to support@jumblejournal.org if the problem persists.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (referralCode) {
    const referralUrl = `https://jumblejournal.org?ref=${referralCode}`;
    return (
      <CopyPasteString
        value={referralUrl}
        fallbackText="No referral code available. Please reach out to support@jumblejournal.org if you'd like to participate in the partner program!"
      />
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <button
        onClick={handleCreateReferralCode}
        disabled={isLoading}
        className="tw-flex tw-items-center tw-justify-center tw-px-4 tw-min-h-control tw-gap-2 tw-rounded-lg tw-bg-white tw-border-2 tw-border-primary-600 tw-text-primary-700 tw-font-semibold tw-text-base tw-w-fit disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
      >
        {isLoading && (
          <span
            className="tw-inline-block tw-mr-3 tw-h-3 tw-w-3 tw-text-center spinner-border"
            role="status"
            aria-hidden="true"
          ></span>
        )}
        Create Referral Code
      </button>
    </div>
  );
};

const InfoModal = (props: { showModal: boolean; setShowModal: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const { showModal, setShowModal } = props;

  const handleCancel = () => {
    setShowModal(false);
  };
  return (
    <Modal show={showModal} onHide={handleCancel}>
      <Modal.Header closeButton>
        <Modal.Title>About Offline Journaling ✍</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="tw-font-bold">
          (New feature ✨) You can upload handwritten entries with your personal Jumble Email Address.
        </p>
        <p className="tw-text-base">
          Take a picture of your journal entry and send it to your personal Jumble Email Address as an attachment.
          Jumble will do the rest. You can add up to five image per email. Make sure the writing is legible and the
          image is centered and well lit. We currently support PNG (.png), JPEG (.jpeg and .jpg), WEBP (.webp), and
          non-animated GIF (.gif).
        </p>
      </Modal.Body>
    </Modal>
  );
};

export default Settings;
